// API clients for the Web Register.
// - Go backend (catalog bundle + server time + scan) — base REACT_APP_GO_BASE_URL, routes under /api.
// - PHP backend (tax list, settings, order write) — base Config.BASE_URL.
// Auth: per user directive the web SENDS auth to Go for forward-compat (Bearer header + token_id +
// login_type in the body), even though Go ignores it today and the POS strips it.

import axios from "axios";
import Config from "../../../Constants/Config";
import { getAuthDetails } from "../../../Common/cookiesHelper";
import { ciUrl } from "../../../Constants/nodeCi";

const RAW_GO = process.env.REACT_APP_GO_BASE_URL || "https://api-go.quickvee.us/";
const GO_BASE = RAW_GO.endsWith("/") ? RAW_GO : RAW_GO + "/"; // tolerate a missing trailing slash
const PHP_BASE = Config.BASE_URL;
const NODE_BASE = Config.BASE_NODE_URL
  ? Config.BASE_NODE_URL.endsWith("/")
    ? Config.BASE_NODE_URL
    : Config.BASE_NODE_URL + "/"
  : "";

// merchant_id + token bundle from encrypted cookies (non-hook; usable outside React).
export function auth() {
  const a = getAuthDetails();
  const rec = a?.LoginGetDashBoardRecordJson;
  const utd = a?.userTypeData || {};
  return {
    merchantId: rec?.data?.merchant_id || "",
    token: rec?.token || "",
    loginType: utd.login_type || "",
    tokenId: utd.token_id || "",
    groupId: rec?.data?.group_id || "",
    employeeId: rec?.employee_id || rec?.data?.id || "",
    employeeName: rec?.employee_name || utd.employee_name || rec?.data?.name || "",
    // Store header for receipts (defensive — omitted from the receipt when absent).
    storeName: rec?.data?.business_name || rec?.data?.store_name || rec?.data?.company_name || "",
    storeAddress: rec?.data?.address || rec?.data?.business_address || "",
    storePhone: rec?.data?.phone || rec?.data?.mobile || rec?.data?.business_phone || "",
  };
}

function form(fields) {
  const body = new URLSearchParams();
  Object.entries(fields).forEach(([k, v]) => body.append(k, v == null ? "" : String(v)));
  return body;
}

function authFields(a) {
  return { merchant_id: a.merchantId, login_type: a.loginType, token_id: a.tokenId };
}

function headers(a) {
  return { "Content-Type": "application/x-www-form-urlencoded", Authorization: `Bearer ${a.token}` };
}

// ---- Go: catalog ----

// Cursor-paginated product list (POS product_list_pagination). Word-for-word with Android's
// getProductListApi: send `last_id` (0 for the first page) and walk forward by the last product's
// id — stable under concurrent catalog writes, unlike offset paging. Result is a PLAIN array.
export async function goFullCatalogPage(lastId, limit) {
  const a = auth();
  const res = await axios.post(
    `${GO_BASE}api/Productapi/product_list_pagination`,
    form({ ...authFields(a), last_id: lastId, limit }),
    { headers: headers(a) }
  );
  return res.data; // { status, result: [Product], pagination }
}

// Delta bundle since a watermark (POS api/products/updates — non-paginated). Products come back as a
// PLAIN array in a SINGLE response (no page loop), matching Android's reverted contract. page/limit
// are sent for wire-compat only; the endpoint returns the whole delta regardless.
export async function goInventoryUpdate(since, page = 1, limit = 10000) {
  const a = auth();
  const res = await axios.post(
    `${GO_BASE}api/products/updates`,
    form({ ...authFields(a), since, page, limit }),
    { headers: headers(a) }
  );
  return res.data; // { status, result: { products:[...], collections, matches, bogos, coupons, loyalty_program, deleted_items, ... } }
}


export async function goServerTime() {
  const a = auth();
  const res = await axios.get(`${GO_BASE}api/time`, {
    params: { login_type: a.loginType, token_id: a.tokenId, merchant_id: a.merchantId },
    headers: { Authorization: `Bearer ${a.token}` },
    responseType: "text",
  });
  return String(res.data || "").trim(); // "YYYY-MM-DD HH:MM:SS"
}

export async function goProductByUpc(upc) {
  const a = auth();
  const res = await axios.post(`${GO_BASE}api/products/upc`, form({ ...authFields(a), upc }), {
    headers: headers(a),
  });
  return res.data;
}

// ---- PHP: reference data + order write ----

export async function phpTaxList() {
  const a = auth();
  const res = await axios.post(ciUrl("settingapi/tax_list", PHP_BASE), form(authFields(a)), { headers: headers(a) });
  return res.data; // { status, result: [ {id,title,percent,displayname,...} ] }
}

export async function phpSaveOrder(payload) {
  const a = auth();
  const res = await axios.post(`${PHP_BASE}Process_order/saveOrderPos`, form(payload), { headers: headers(a) });
  return res.data; // { status, message, order_id, id }
}

// Live quantity lookup for a product/variant — the POS long-press "Current Quantity" API.
export async function phpQtyById({ pid, vid = "" }) {
  const a = auth();
  const res = await axios.post(
    ciUrl("app/get_Qty_ById", PHP_BASE),
    form({ ...authFields(a), vid, pid }),
    { headers: headers(a) }
  );
  return res.data; // { status, message, data: { quantity, trackqnty, isstockcontinue } }
}

// ---- PHP: cash-drawer operations (Android register ⋮ menu: Drop Cash / Pay In / Vendor Payout) ----
// Android getCurrentDateTimeWithTimeStamp / getCurrentDateAndTime(0): "yyyy-MM-dd HH:mm:ss" (device local).
export function localDateTimeStamp(d = new Date()) {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}
// Android CashPointViewModel.setCashPointRequest → Shift_inout/drop_cash. adv_id (Google advertising
// id) and shift_setting (SHIFT_ASSIGN_STATUS) are device-only values; the browser sends them blank,
// exactly what a POS without them stores.
export async function phpDropCash({ amount, reason }) {
  const a = auth();
  const res = await axios.post(
    ciUrl("Shift_inout/drop_cash", PHP_BASE),
    form({
      ...authFields(a),
      employee_id: String(a.employeeId || ""),
      date_added: localDateTimeStamp(),
      drop_cash: String(amount),
      reason: String(reason || ""),
      adv_id: "",
      admin_id: String(a.merchantId || ""),
      shift_setting: "",
      payout_amt: "",
    }),
    { headers: headers(a) }
  );
  return res.data; // { status, message, ... }
}
// Android PayInViewModel.setPayInRequest → Shift_inout/payin_cash.
export async function phpPayInCash({ amount, reason }) {
  const a = auth();
  const res = await axios.post(
    ciUrl("Shift_inout/payin_cash", PHP_BASE),
    form({
      ...authFields(a),
      admin_id: String(a.merchantId || ""),
      employee_id: String(a.employeeId || ""),
      adv_id: "",
      date_added: localDateTimeStamp(),
      payin_cash: String(amount),
      reason: String(reason || ""),
      shift_setting: "",
    }),
    { headers: headers(a) }
  );
  return res.data;
}
// Android vendorPayoutDialog → app/vendor_payment_list (vendors to pick from).
export async function phpVendorList() {
  const a = auth();
  const res = await axios.post(ciUrl("app/vendor_payment_list", PHP_BASE), form(authFields(a)), { headers: headers(a) });
  return res.data; // { status, result: [ { id, name, ... } ] }
}
// Android EmployeeListRepository.fetchEmployeeList → App/employee_list: every employee assigned to
// this store (incl. `pin` + `role`). The POS caches it in Room and validates the manager PIN LOCALLY
// (PasscodeViewModel.setPasscodeRequestLocal); the web fetches it when the PIN dialog opens.
export async function phpEmployeeList() {
  const a = auth();
  const res = await axios.post(ciUrl("App/employee_list", PHP_BASE), form(authFields(a)), { headers: headers(a) });
  return res.data; // { status, result: [ { id, f_name, pin, role, ... } ] }
}
// Android VendorPayoutViewModel.setVendorPayoutRequest → app/vendor_payout (pay_by is always "cash").
export async function phpVendorPayout({ vendorId, amount, remark }) {
  const a = auth();
  const res = await axios.post(
    `${PHP_BASE}app/vendor_payout`,
    form({
      ...authFields(a),
      vendor_id: String(vendorId),
      pay_amount: String(amount),
      payment_datetime: localDateTimeStamp(),
      remark: String(remark || ""),
      pay_by: "cash",
      employee_id: String(a.employeeId || ""),
      adv_id: "",
      shift_setting: "",
    }),
    { headers: headers(a) }
  );
  return res.data;
}

// Stock-prompt "Update quantity" — the SAME API the POS uses (productapi/save_instant_po, Android
// VariantDetailViewModel.setSaveInstantPoRequest): an instant PO that sets the on-hand qty.
export async function phpSaveInstantPo({ productId, variantId = "", qty, price, description = "stockprompt" }) {
  const a = auth();
  const res = await axios.post(
    `${PHP_BASE}productapi/save_instant_po`,
    form({
      ...authFields(a),
      product_id: String(productId),
      variant_id: String(variantId || ""),
      description,
      qty: String(qty),
      price: String(price ?? ""),
      emp_id: String(a.employeeId || ""),
    }),
    { headers: headers(a) }
  );
  return res.data; // { status, message }
}

// Customer lookup by phone, email OR name — the SAME API the POS uses (App/find_customer). The
// controller LIKE-matches phone_no against phone, email against email, and `name` across name/email/
// phone (see App::find_customer). POS routes the search box to exactly one of the three per keystroke.
export async function phpFindCustomer({ phone_no = "", email = "", name = "" }) {
  const a = auth();
  const res = await axios.post(
    ciUrl("App/find_customer", PHP_BASE),
    form({ ...authFields(a), phone_no, email, name, group_id: a.groupId }),
    { headers: headers(a) }
  );
  return res.data; // { status, result: [ customer ] }
}

// Create a customer from the register — the SAME endpoint the POS add-customer dialog posts to
// (App/save_customer_details). Android sends the full address/dob/notes set even though its register
// dialog only collects name/phone/email, so the blanks are sent verbatim to keep the wire identical.
// `customer_id` empty = CREATE (the same endpoint updates when an id is supplied).
export async function phpSaveCustomerDetails({ fname, lname = "", email, phone_no, createdDate }) {
  const a = auth();
  const res = await axios.post(
    `${PHP_BASE}App/save_customer_details`,
    form({
      ...authFields(a),
      fname,
      lname,
      email,
      phone_no,
      address_line_1: "",
      address_line_2: "",
      city: "",
      state: "",
      pincode: "",
      dob: "",
      notes: "",
      customer_id: "",
      order_id: "",
      note: "",
      created_date: createdDate,
      group_id: a.groupId,
    }),
    { headers: headers(a) }
  );
  return res.data; // { status, message, result: { customer_id } }
}

// Quick-Add: create a REAL catalog product from the register (Productapi/quick_add_product_new).
// `tax` is a comma-joined list of tax ids; `cat_id` is the store's "Quickadd" category.
// Unlike a Custom Item (a one-off cart line), this product persists and can be scanned again.
export async function phpQuickAddProduct({ title, price, tax = "", upc, categoryId }) {
  const a = auth();
  const res = await axios.post(
    `${PHP_BASE}Productapi/quick_add_product_new`,
    form({
      ...authFields(a),
      title,
      price,
      tax,
      upc,
      emp_id: a.employeeId || "",
      cat_id: categoryId,
    }),
    { headers: headers(a) }
  );
  return res.data; // { status, message, product_id }
}

// Gift card list for the merchant/group — the register looks up a card's balance by number
// (mirrors giftCardListViewModel + Gift_Ebt_api/get_giftcard).
export async function phpGiftCards() {
  const a = auth();
  const res = await axios.post(
    ciUrl("Gift_Ebt_api/get_giftcard", PHP_BASE),
    form({ ...authFields(a), group_id: a.groupId }),
    { headers: headers(a) }
  );
  return res.data; // { status, result: [ { number, amount(=balance), ... } ] }
}

// Email / SMS a receipt (Android orders/payment_mail_sms). type = "email" | "sms".
export async function phpEmailSmsReceipt({ orderId, type, email = "", number = "", html }) {
  const a = auth();
  const res = await axios.post(
    `${PHP_BASE}orders/payment_mail_sms`,
    form({
      ...authFields(a),
      group_id: a.groupId,
      order_id: orderId,
      status: "Approved",
      type,
      email_id: email,
      number,
      html_data: html,
    }),
    { headers: headers(a) }
  );
  return res.data;
}

// Coupon list — the SAME endpoint Android's register coupon dialogs use (Couponapi_live/
// get_coupon_details, a SELECT * over `coupon` with date_expire >= today). Carries the QV-4738
// item-coupon fields the Go bundle doesn't (can_combine_discounts, items map) — the item-coupon
// dialog fetches this fresh on every open, exactly like Android's ItemCouponDialog.
export async function phpCouponList() {
  const a = auth();
  const res = await axios.post(
    `${PHP_BASE}Couponapi_live/get_coupon_details`,
    form(authFields(a)),
    { headers: headers(a) }
  );
  return res.data; // { status, result: [ {id,name,discount,flag,type,items,can_combine_discounts,maximum_discount,date_valid,date_expire,min_amount,...} ] }
}

// ---- PHP: in-store order history + refunds (Android receipt/refund flow endpoints) ----

// Ranged in-store order list — Android's filtered in-store list (orders/order_list, order_list_type
// "instore"). Returns customer info per row (`name` = billing/deliver coalesced, customer_id,
// customer_phone) + order_number/amt/refund_amount/live_status. Dates are yyyy-MM-dd.
export async function phpOrderList({ startDate, endDate, searchValue = "" }) {
  const a = auth();
  const res = await axios.post(
    ciUrl("orders/order_list", PHP_BASE),
    form({
      ...authFields(a),
      start_date: startDate,
      end_date: endDate,
      order_list_type: "instore",
      order_status: "",
      search_value: searchValue,
      is_paid: 1,
      is_split_payment: 0,
      limit: 100,
      page_no: 1,
      group_id: a.groupId,
    }),
    { headers: headers(a) }
  );
  return res.data; // { status, result: [ {id, order_id, order_number, name, customer_id, amt, refund_amount, merchant_time, live_status, payment_id, ...} ] }
}

// Partial ($ amount) refund — Android Process_refund_api/update_partial_refund. `fields` fully
// built by engine/refund.js buildPartialRefund.
export async function phpPartialRefund(fields) {
  const a = auth();
  const res = await axios.post(`${PHP_BASE}Process_refund_api/update_partial_refund`, form({ ...authFields(a), ...fields }), {
    headers: headers(a),
  });
  return res.data;
}

// Void a transaction — Android orders/updateVoidOrder (server sets m_status=5 + cvvResult 'void'
// and reverses loyalty/etc). Card charges must be reversed at the gateway FIRST (open batch).
export async function phpVoidOrder(orderId) {
  const a = auth();
  const res = await axios.post(
    `${PHP_BASE}orders/updateVoidOrder`,
    form({ ...authFields(a), order_id: orderId, group_id: a.groupId }),
    { headers: headers(a) }
  );
  return res.data;
}

// Order status — Android orderStatusUpdateApi (orders/change_order_status). Used after a FULL amount-tab
// refund to mark the order REFUNDED (7): update_partial_refund never sets m_status itself.
export async function phpChangeOrderStatus(orderId, status) {
  const a = auth();
  const res = await axios.post(
    ciUrl("orders/change_order_status", PHP_BASE),
    form({ ...authFields(a), employee_id: a.employeeId, order_id: orderId, status, group_id: a.groupId }),
    { headers: headers(a) }
  );
  return res.data;
}

// Gift-card balance adjust — Android addRemoveGiftCard (Gift_Ebt_api/adjust_giftcard). A gift-card
// REFUND credits the card back ("Refund"/credit), mirroring adjustGiftCardViewModel.setAdjustGiftCardAmount.
export async function phpAdjustGiftCard({ number, amount, orderId, type = "credit", location = "Refund" }) {
  const a = auth();
  const res = await axios.post(
    ciUrl("Gift_Ebt_api/adjust_giftcard", PHP_BASE),
    form({
      ...authFields(a),
      number,
      user_id: "",
      amount: String(amount),
      emp_id: a.employeeId || "",
      // bare LOCAL datetime like Android getCurrentDateAndTime (NOT ISO/UTC — PHP strtotime trap)
      created_at: (() => {
        const d = new Date();
        const p = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
      })(),
      location,
      type,
      desc: orderId || "",
      group_id: a.groupId,
    }),
    { headers: headers(a) }
  );
  return res.data;
}

// Full order detail — cart_data rows (incl. refund_qty/is_refunded/item_coupon_json), order_detail,
// refund_data history, split_payments (Android orders/order_details).
export async function phpOrderDetails(orderId) {
  const a = auth();
  const res = await axios.post(
    ciUrl("orders/order_details", PHP_BASE),
    form({ ...authFields(a), order_id: orderId, group_id: a.groupId }),
    { headers: headers(a) }
  );
  return res.data; // { status, result: { cart_data, order_detail, refund_data, merchant_details, split_payments } }
}

// Item-level refund — Android Process_refund_api/save_line_item_refund. `fields` comes fully built
// from engine/refund.js buildItemLevelRefund (exact Android wire contract).
export async function phpItemLevelRefund(fields) {
  const a = auth();
  const res = await axios.post(`${PHP_BASE}Process_refund_api/save_line_item_refund`, form({ ...authFields(a), ...fields }), {
    headers: headers(a),
  });
  return res.data; // { status, message, ... }
}

// Store options (users / user_options row) — the same endpoint the admin Store-Option screen uses.
// The register only needs `user_data.enable_order_number` (the web mirror of Android's
// getStoreDetailsData().enableOrderNumber) to gate server-side sequential order-number assignment in
// saveOrderPos. The handler keys solely off merchant_id (authFields carries it).
export async function phpStoreOptions() {
  const a = auth();
  const res = await axios.post(
    `${PHP_BASE}Store_setting_react_api/get_store_options_data`,
    form(authFields(a)),
    { headers: headers(a) }
  );
  return res.data; // { status, user_data: { enable_order_number, ... }, user_option_data, ... }
}

// Store details — Android StoreDetailsEntityLocal source. The ONLY register-reachable endpoint that
// carries the NCA / Dual Price switch (`user_options.is_surcharge` 0 off | 1 NCA | 2 dual price,
// `surcharge_per`, `debit_surcharge`); inventory_register_setting returns inventory_setting.* only.
export async function phpStoreDetails() {
  const a = auth();
  const res = await axios.post(ciUrl("Settingapi/store_details", PHP_BASE), form(authFields(a)), { headers: headers(a) });
  return res.data; // { status, result: { ...users.*, is_surcharge, surcharge_per, debit_surcharge, ... } }
}

// Register settings (surcharge/NCA + cashback config). Same endpoint the admin uses. Field names are
// mapped defensively in registerSettings.js since the live shape isn't versioned here.
export async function phpRegisterSettings() {
  const a = auth();
  const res = await axios.post(
    `${PHP_BASE}ProfileReact_setup/inventory_register_setting`,
    form({ ...authFields(a), group_id: a.groupId }),
    { headers: headers(a) }
  );
  return res.data;
}

// ---- Node: Dezi / mKonnekt manufacturer-discount lookup (QV-7124) ----
// PUBLIC storefront route — deliberately NOT behind authenticateRequest (the online storefront calls
// it for guests), so NO Authorization header is sent. The merchant is identified by `merchant.rcn`
// in the JSON body; the optional top-level `merchant_id` is sent too when this session has one, so
// the backend can attribute the lookup to the store.
//
// The repository contract is "never throw, never block checkout": ANY failure resolves to a
// not-successful result which the caller maps to EMPTY. A manufacturer-discount lookup must never be
// able to stop a sale.
export async function nodeDeziDiscounts(request, { timeoutMs = 15000 } = {}) {
  try {
    let merchantId = "";
    try {
      merchantId = auth().merchantId;
    } catch {
      /* no session — the rcn alone identifies the store */
    }
    const body = merchantId ? { ...request, merchant_id: merchantId } : request;
    const res = await axios.post(`${NODE_BASE}Mkonnekt_discount/get_discounts`, body, {
      headers: { "Content-Type": "application/json" },
      timeout: timeoutMs,
    });
    return { ok: true, body: res.data };
  } catch {
    return { ok: false, body: null };
  }
}

// ---- Node: Payroc PIN-pad (Ingenico) terminal card-present payment proxy ----
// The browser can't hold the Payroc api_key (and would hit CORS), so it calls the Node
// backend which resolves per-merchant creds + device serial and talks to Payroc v1.
async function nodePost(path, fields) {
  const a = auth();
  const res = await axios.post(
    `${NODE_BASE}${path}`,
    { ...fields, merchant_id: a.merchantId },
    { headers: { "Content-Type": "application/json", Authorization: `Bearer ${a.token}` } }
  );
  return res.data;
}

// Start a device payment instruction → { status, payment_instruction_id, ... }.
export function nodeCreateTerminalPayment({ orderId, amount, cardType, operator, currency = "USD", idempotencyKey }) {
  return nodePost("PosTerminalPayment/create", {
    order_id: orderId,
    amount,
    card_type: cardType,
    operator,
    currency,
    idempotency_key: idempotencyKey,
  });
}

// Poll a payment instruction → { status, instruction_status, payment_id, error_message, ... }.
export function nodeTerminalPaymentStatus({ paymentInstructionId }) {
  return nodePost("PosTerminalPayment/status", { payment_instruction_id: paymentInstructionId });
}

// Read the resulting payment → { status, approved, declined, card_type, cvv_result, tip_amount, raw, ... }.
export function nodeTerminalPaymentDetails({ paymentId }) {
  return nodePost("PosTerminalPayment/details", { payment_id: paymentId });
}

// Recover a charge by client order id (missing-transaction guard) → { status, found, approved, ... }.
export function nodeReconcileTerminalPayment({ orderId }) {
  return nodePost("PosTerminalPayment/reconcile", { order_id: orderId });
}

// Void/reverse a payment (open batch) — used to cancel an orphaned charge the cashier chooses not to keep.
export function nodeReverseTerminalPayment({ paymentId, amount }) {
  return nodePost("PosTerminalPayment/reverse", { payment_id: paymentId, amount });
}

// Refund a prior card payment by its Payroc payment id (settled OR open batch — the gateway
// auto-voids when the batch is still open). The web equivalent of Android's PAX terminal refund:
// money moves back to the card FIRST, then the refund is recorded via save_line_item_refund.
// → { status, payment_id, card_type, card_last4, raw, reason? }
export function nodeRefundTerminalPayment({ paymentId, amount, description }) {
  return nodePost("PosTerminalPayment/refund", { payment_id: paymentId, amount, description });
}
